import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { FormsItemModalComponent } from '../forms-item-modal/forms-item-modal.component';
import { FormsGlobals } from '../../../models/forms/forms.enum';
import { FormsService } from '../forms.service';
import { MessagingService } from '../../../services/messaging.service';
import { SignaturePad } from '@tdo-inc/tdox-signaturepad/dist/tdox-signaturepad';
import { EnumValidValueItemModel } from '../../../models/enum/enum-valid-value-item.model';
import { FormItemDataModel } from '../../../models/forms/data/form-item-data.model';
import { FormResultDataModel } from '../../../models/forms/data/form-result-data.model';
import { FormParagraphDataModel } from '../../../models/forms/data/form-paragraph-data.model';
import { FormItemActionStepConfigurationModel } from '../../../models/forms/form-item-action-step-configuration.model';
import { MatSelectChange } from '@angular/material/select';
import { NotesService } from '../../../services/notes.service';
import { PredefinedNoteDataModel } from '../../../models/notes/predefined-note-data.model';
import { PredefinedNoteSetDataModel } from '../../../models/notes/predefined-note-set-data.model';
import { TdoDropdownModel } from '../../../tdo-controls/common/tdo-basic-dropdown/models/tdo-dropdown.model';
import { Globals } from '../../../globals';
import { StaticDataService } from '../../../services/static-data.service';
import { ITdoFileHandle } from '../../../models/ui/i-tdo-file-handle';
import { FileHelperService } from '../../../services/file-helper.service';
import { MessagingConstants } from '../../../models/MessagingConstants';
import { DocumentUploadModel } from '../../../models/document/document-upload.model';
import { DocumentClassEnum } from '../../../models/enum/document/document-class.enum';
import { DocumentTypeEnum } from '../../../models/enum/document/document-type.enum';
import { DocumentFileTypeEnum } from '../../../models/enum/document/document-file-type.enum';
import { FileManagerService } from '../../file-manager/file-manager.service';
import { FormsFullSizeImageDialogComponent } from '../dialogs/forms-full-size-image-dialog/forms-full-size-image-dialog.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormActionNameEnum } from '../../../models/enum/forms/form-action-name.enum';
import { TdoNoteAssociatedObject, TdoNoteSaveModel } from '../../../main-interface/controls/tdo-notes/models/tdo-note-save-model';
import { ToothUiManagerModel } from '../../tdo-shared/tooth-picker/tooth-ui-manager/tooth-ui-manager.model';
import { FormParagraphConfigurationModel } from '../../../models/forms/form-paragraph-configuration.model';

@Component({
  selector: 'app-forms-item',
  templateUrl: './forms-item.component.html',
  styleUrls: ['./forms-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormsItemComponent implements OnInit, AfterViewInit {
  @ViewChild('fileInput') fileInput: ElementRef;

  @Output() saveDataNoteEvent = new EventEmitter<any>();

  @Input() isLocked: boolean;
  @Input() dataFormItem: FormItemDataModel;
  @Input() sideBySide: boolean;
  @Input() paragraphOrder: number;
  @Input() itemIndex: number;
  @Input() saveEmitter: EventEmitter<any>;
  @Input() dataFormID: string;
  @Input() actionsEnabled: boolean;
  @Input() CaseWorkItemFormsId: string;
  @Input() height: number;
  @Input() relatedRepeatingParagraph: FormParagraphConfigurationModel;

  FormActionNameEnum = FormActionNameEnum;
  isUploading = false;
  showHiddenText: boolean = false;
  inputTimeout: any = false;
  isPatient: boolean = false;
  patientId: string;

  showPad: boolean = false;
  resetSignature: boolean = true;
  signaturePadOptions: object = {
    'minWidth': 2,
    'canvasWidth': 400,
    'canvasHeight': 150,
    'backgroundColor': 'rgb(255, 255, 255)',
    'penColor': 'rgb(0, 0, 0)',
    'velocityFilterWeight': 0.7,
    'touchAction': 'none' // Prevents scrolling while signing on touch devices
  };

  @ViewChild('signaturePad') signaturePad: SignaturePad;
  @ViewChild('signatureContainer') signatureContainer: ElementRef;

  toothNumbers: string[] = [];
  messageSubscription: any;
  summaryEventInterval: any;
  resizeTimeout: any;

  filteredSummaryItems: any[] = [];
  summaryItems: FormItemDataModel[] = [];

  selectedPredefinedNote: PredefinedNoteDataModel;
  dataNoteSet: PredefinedNoteSetDataModel;
  notesDropdownData: TdoDropdownModel[] = [];
  imageData;
  signatureDrawn: boolean;
  isActionExecuting: boolean = false;
  currentPatientName: string ='';

  constructor(
    public formsGlobals: FormsGlobals,
    private formsService: FormsService,
    private messagingService: MessagingService,
    public dialog: MatDialog,
    private notesService: NotesService,
    private globals: Globals,
    private staticDataService: StaticDataService,
    private fileHelperService: FileHelperService,
    public fileService: FileManagerService,
    private matDialog: MatDialog
  ) {
  }

  ngAfterViewInit() {
    if (this.signaturePad) {
      if (this.isLocked) {
        this.signaturePad.off();
      }
      
      // Make sure signature pad size is properly set after view initialization
      // Use a longer timeout for iPad devices
      setTimeout(() => {
        this.setupSignaturePadSize();
        this.resizeSignaturePad();
        
        // Handle iOS Safari by forcing a redraw after the component is loaded
        // This is especially helpful for iPad devices
        if (this.isIOSDevice()) {
          console.log('iOS device detected - applying special signature pad handling');
          // Force redraw for iOS devices
          setTimeout(() => {
            this.resizeSignaturePad();
            // Add a small touch handler to wake up the canvas
            if (this.signatureContainer && this.signatureContainer.nativeElement) {
              const container = this.signatureContainer.nativeElement;
              const touchHandler = () => {
                console.log('Touch activated on signature pad container');
                container.removeEventListener('touchstart', touchHandler);
              };
              container.addEventListener('touchstart', touchHandler);
            }
          }, 800);
        }
      }, 300);
    }
  }

  ngOnInit() {
    this.patientId = this.globals.currentPatientId;
    if (this.globals.ThePatient?.dataContact) {
      this.currentPatientName = this.globals.ThePatient.dataContact.FirstName + ' ' + this.globals.ThePatient.dataContact.LastName;
    }

    if (this.dataFormItem.cfgDFormItem.enumFormControlID === this.formsGlobals.CONTROL_MACRO_NOTES_DROPDOWN) {
      this.getMacroNotes();
    }

    const isPatientRole = localStorage.getItem('isPatientRole');
    if (isPatientRole && isPatientRole === 'true') {
      this.isPatient = true;
    }

    // Initial signature pad size setup
    this.setupSignaturePadSize();
    
    // Add resize event listener to adjust signature pad size on window resize
    window.addEventListener('resize', this.handleWindowResize.bind(this));

    this.showPad = true;

    for (let i = 1; i <= 32; i++) {
      this.toothNumbers.push(`${i}`);
    }

    this.messageSubscription = this.messagingService.componentMessageCall
      .subscribe(response => {

        if (response.event == `FORMS_ITEM_NEEDS_SUMMARY_${this.CaseWorkItemFormsId}`) {
          if (response.data.dataFormItemId !== this.dataFormItem.dataFormItemId) {
            this.messagingService.sendComponentMessage(`FROMS_RETURN_ITEM_SUMMARY_${response.data.dataFormItemId}`, { dataFormItem: this.dataFormItem });
          }
        }

        if (response.event == `FROMS_RETURN_ITEM_SUMMARY_${this.dataFormItem.dataFormItemId}`) {
          this.summaryItems.push(response.data.dataFormItem);
          this.buildResultSummary();
        }
        
        // Resize signature pad when a form is loaded
        if (response.event === 'FORMS_LOADED' && 
            this.dataFormItem.cfgDFormItem.enumFormControlID === this.formsGlobals.CONTROL_SIGNATURE) {
          setTimeout(() => {
            this.setupSignaturePadSize();
            this.resizeSignaturePad();
          }, 300);
        }

      });

    if (this.dataFormItem.cfgDFormItem.ValidValue) {
      this.dataFormItem.cfgDFormItem.ValidValue.enumValidValueItems =
        this.dataFormItem.cfgDFormItem.ValidValue.enumValidValueItems.sort((a, b) => {
          return a.ItemOrder - b.ItemOrder;
        });
    }

    this.saveEmitter.subscribe(e => {
      this.saveItem();
    });
    this.setUpResults();
  }

  ngDestroy() {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }

    if (this.summaryEventInterval) {
      clearInterval(this.summaryEventInterval);
    }
    
    // Remove resize event listener when component is destroyed
    window.removeEventListener('resize', this.handleWindowResize.bind(this));
  }

  getTextAlign(item: FormItemDataModel) {
    if (item.cfgDFormItem.enumLabelAligmentID == '00000036-1000-0000-0000-000000000001') {
      return 'left';
    } else if (item.cfgDFormItem.enumLabelAligmentID == '00000036-1000-0000-0000-000000000002') {
      return 'right';
    } else if (item.cfgDFormItem.enumLabelAligmentID == '00000036-1000-0000-0000-000000000003') {
      return 'center';
    } else {
      return 'left';
    }
  }

  clearSignature() {
    this.signatureDrawn = false;
    this.dataFormItem.RESULT.Result = null;
    this.resetSignature = true;
    this.saveResult(this.dataFormItem.RESULT);
  }
  signatureDrawComplete() {
    console.log('signatureDrawComplete: ', this.signatureDrawn);
    // will be notified of szimek/signature_pad's onEnd event
    if (this.signatureDrawn) {
      // For iPad: restore normal scrolling behavior
      if (this.isIOSDevice()) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
      
      // Save the signature
      try {
        this.dataFormItem.RESULT.Result = this.signaturePad.toDataURL();
        this.saveResult(this.dataFormItem.RESULT);
        this.resetSignature = false;
      } catch (error) {
        console.error('Error converting signature to data URL:', error);
        
        // Try again with a delay on iOS devices (can help with timing issues)
        if (this.isIOSDevice()) {
          setTimeout(() => {
            try {
              this.dataFormItem.RESULT.Result = this.signaturePad.toDataURL();
              this.saveResult(this.dataFormItem.RESULT);
              this.resetSignature = false;
            } catch (retryError) {
              console.error('Error on retry converting signature:', retryError);
              // Leave resetSignature as true so user can try again
            }
          }, 100);
        }
      }
    }
  }

  buildResultSummary() {
    this.filteredSummaryItems = [];
    var itemsWithDefaultValues = this.summaryItems.filter(item => {
      return (item.cfgDFormItem.DefaultValue != null && item.cfgDFormItem.DefaultValue !== "") && (item.cfgDFormItem.DefaultValue !== item.RESULT.Result);
    });

    var newArr = [];

    itemsWithDefaultValues.forEach(itemWithDefault => {
      var tmpItem = JSON.parse(JSON.stringify(itemWithDefault));
      var parentItem = this.summaryItems.find(item => {
        return tmpItem.cfgDFormItem.ParentDFormItemId === item.cfgDFormItem.DFormItemId;
      })
      tmpItem.DATA_PARENT_ITEM = parentItem;
      this.filteredSummaryItems.push(tmpItem);
      newArr.push(tmpItem);
    });

    //console.log('items with defaults', newArr);

    var summary = ``;

    newArr.forEach(summaryItem => {
      var summaryForItem = '';

      if (summaryItem.DATA_PARENT_ITEM) {
        summaryForItem = `${summaryItem.DATA_PARENT_ITEM.cfgDFormItem.Label}: `;
      }

      if (summaryItem.RESULT) {
        summaryForItem += `${summaryItem.RESULT.Result}`;
      } else if (summaryItem.RESULTS) {
        summaryItem.RESULTS.forEach((result, index) => {
          summaryForItem += `${result.Result}`;
          if (index !== summaryItem.RESULTS.length - 1) {
            summaryForItem += `, `;
          }
        });
      }

      summary += `${summaryForItem}\n`;
    });

    this.dataFormItem.RESULT.Result = summary;
  }

  openSummaryWindow() {
    console.log('open the summary window!', this.filteredSummaryItems);
    // build a temp form for these items

    var summaryParagraph = {
      items: []
    };

    for (let i = 0; i < this.filteredSummaryItems.length; i++) {

      var item = JSON.parse(JSON.stringify(this.filteredSummaryItems[i]));
      var parentItem = null;
      if (item.DATA_PARENT_ITEM) {
        parentItem = item.DATA_PARENT_ITEM;
      }

      item.cfgDFormItem.ParagraphRow = i;
      item.cfgDFormItem.ParagraphColumn = 0;

      if (parentItem) {
        parentItem.cfgDFormItem.ParagraphRow = i;
        parentItem.cfgDFormItem.ParagraphColumn = 0;
        item.cfgDFormItem.ParagraphColumn = 1;
        summaryParagraph.items.push(parentItem);
      }

      summaryParagraph.items.push(item);
    }
    //FORMS_OPEN_SUMMARY_

    this.messagingService.sendComponentMessage(`FORMS_OPEN_SUMMARY_${this.CaseWorkItemFormsId}`, { summaryParagraph: summaryParagraph });
  }


  sendResultSummaryEvent() {
    this.summaryItems = [];
    this.messagingService.sendComponentMessage(`FORMS_ITEM_NEEDS_SUMMARY_${this.CaseWorkItemFormsId}`, { dataFormItemId: this.dataFormItem.dataFormItemId });
  }

  startSummaryEventInterval() {
    // We really need to get away from using timers instead of using events or data binding.
    this.summaryEventInterval = setInterval(_ => {
      this.sendResultSummaryEvent();
    }, 2000);
  }

  setUpResults() {
    let type = this.dataFormItem.cfgDFormItem.enumFormControlID;

    if (type == this.formsGlobals.CONTROL_GROUP_HEADING_WITH_SUMMARY) {
      let result: FormResultDataModel;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
        //console.log('resetSignature to false 3');
        this.resetSignature = false;
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
        //console.log('resetSignature to false 4');
        this.resetSignature = false;
      } else {
        result = this.createBlankResult();
        this.resetSignature = true;
      }
      this.dataFormItem.RESULT = result;
      this.dataFormItem.RESULTS = null;
      this.startSummaryEventInterval();
    }

    else if (type == this.formsGlobals.CONTROL_SIGNATURE) {
      let result: FormResultDataModel;
      //console.log('signature dataFormItem', JSON.stringify(this.dataFormItem));
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
        //console.log('resetSignature to false 1');
        this.resetSignature = false;
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
        //console.log('resetSignature to false 2: ', result);
        if (result.Result && result.Result !== '' && result.Result !== null) {
          this.resetSignature = false;
        }
      } else if (this.dataFormItem.RESULT && this.dataFormItem.RESULT.Result && this.dataFormItem.RESULT.Result !== '') {
        result = this.dataFormItem.RESULT;
        this.resetSignature = false;

        //console.log('resetSignature to false 3: ', result);
      } else if (this.dataFormItem.RESULT && this.dataFormItem.RESULT.Result && this.dataFormItem.RESULT.Result !== '') {
        result = this.dataFormItem.RESULT;
        this.resetSignature = false;

        //console.log('resetSignature to false 3: ', result);
      } else {
        result = this.createBlankResult();
        this.resetSignature = true;
      }
      this.dataFormItem.RESULT = result;
      this.dataFormItem.RESULTS = null;
      //console.log('signature result', this.dataFormItem.RESULT);
      if (this.dataFormItem.RESULT?.Result && this.dataFormItem.RESULT?.Result != '' && !this.resetSignature)
        console.log('Signature IMG should display');
      else
        console.log('Signature IMG should not display');
    }

    else if (
      type == this.formsGlobals.CONTROL_RADIO_LIST ||
      type == this.formsGlobals.CONTROL_COMBO_LIST ||
      type == this.formsGlobals.CONTROL_SMALL_CHECK_BOX ||
      type == this.formsGlobals.CONTROL_CHECK_BOX ||
      type == this.formsGlobals.CONTROL_DATE ||
      type == this.formsGlobals.CONTROL_NUMERIC_INTEGER ||
      type == this.formsGlobals.CONTROL_NUMERIC_DECIMAL ||
      type == this.formsGlobals.CONTROL_CURRENCY ||
      type == this.formsGlobals.CONTROL_HIDDENINPUT ||
      type == this.formsGlobals.CONTROL_PHONE
    ) {
      let result: FormResultDataModel;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
      } else {
        result = this.createBlankResult();
        if (this.dataFormItem.cfgDFormItem.DefaultValue != null && this.dataFormItem.cfgDFormItem.DefaultValue !== "") {
          result.Result = this.dataFormItem.cfgDFormItem.DefaultValue;
        }
      }
      this.dataFormItem.RESULT = result;
      this.dataFormItem.RESULTS = null;
    }

    else if (type == this.formsGlobals.CONTROL_BLOOD_PRESSURE) {
      let result: FormResultDataModel;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
      } else {
        result = this.createBlankResult();
        if (this.dataFormItem.cfgDFormItem.DefaultValue != null && this.dataFormItem.cfgDFormItem.DefaultValue !== "") {
          result.Result = this.dataFormItem.cfgDFormItem.DefaultValue;
        }
      }
      this.dataFormItem.RESULT = result;

      if (this.dataFormItem.RESULT.Result.indexOf('/')) {
        let top = this.dataFormItem.RESULT.Result.split('/')[0];
        let bottom = this.dataFormItem.RESULT.Result.split('/')[1];
        this.dataFormItem.RESULT_TOP = top;
        this.dataFormItem.RESULT_BOTTOM = bottom;
      } else {
        this.dataFormItem.RESULT_TOP = '';
        this.dataFormItem.RESULT_BOTTOM = '';
      }
      this.dataFormItem.RESULTS = null;
    }

    else if (type == this.formsGlobals.CONTROL_VITAL_SIGNS) {
      let result: FormResultDataModel;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
      } else {
        result = this.createBlankResult();
        if (this.dataFormItem.cfgDFormItem.DefaultValue != null && this.dataFormItem.cfgDFormItem.DefaultValue !== "") {
          result.Result = this.dataFormItem.cfgDFormItem.DefaultValue;
        }
      }
      this.dataFormItem.RESULT = result;

      if (this.dataFormItem.RESULT.Result.indexOf('/')) {
        let one = this.dataFormItem.RESULT.Result.split('/')[0];
        let two = this.dataFormItem.RESULT.Result.split('/')[1];
        let three = this.dataFormItem.RESULT.Result.split('/')[2];
        this.dataFormItem.RESULT_VITAL_ONE = one;
        this.dataFormItem.RESULT_VITAL_TWO = two;
        this.dataFormItem.RESULT_VITAL_THREE = three;
      } else {
        this.dataFormItem.RESULT_VITAL_ONE = '';
        this.dataFormItem.RESULT_VITAL_TWO = '';
        this.dataFormItem.RESULT_VITAL_THREE = '';
      }
      this.dataFormItem.RESULTS = null;
    }

    else if (
      type == this.formsGlobals.CONTROL_DATE_TIME ||
      type == this.formsGlobals.CONTROL_TIME
    ) {
      let result: FormResultDataModel;
      let isDateFlag = false;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
        isDateFlag = true;
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
        isDateFlag = true;
      } else {
        result = this.createBlankResult();
        if (this.dataFormItem.cfgDFormItem.DefaultValue != null && this.dataFormItem.cfgDFormItem.DefaultValue !== "") {
          result.Result = this.dataFormItem.cfgDFormItem.DefaultValue;
        }
      }
      this.dataFormItem.RESULT = result;
      this.dataFormItem.RESULTS = null;

      let isValid = !isNaN(new Date(this.dataFormItem.RESULT.Result).getTime());

      if (!isValid) {
        this.dataFormItem.RESULT.Result = new Date().toString();
      }

      if (isDateFlag) {
        let date = new Date(this.dataFormItem.RESULT.Result);
        this.dataFormItem.RESULT_HOUR = date.getHours();
        this.dataFormItem.RESULT_MINUTES = date.getMinutes();
      }
    }

    else if (
      type == this.formsGlobals.CONTROL_TEXT_BOX ||
      type == this.formsGlobals.CONTROL_TEXT_AREA
    ) {
      let result: FormResultDataModel;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
      } else {
        result = this.createBlankResult();
        if (this.dataFormItem.cfgDFormItem.DefaultValue != null && this.dataFormItem.cfgDFormItem.DefaultValue !== "") {
          result.Result = this.dataFormItem.cfgDFormItem.DefaultValue;
        }
      }
      this.dataFormItem.RESULT = result
      this.dataFormItem.RESULTS = null;
    }
    else if (
      type == this.formsGlobals.CONTROL_CHECK_BOX_LIST ||
      type == this.formsGlobals.CONTROL_TOOTH_PICKER
    ) {
      this.dataFormItem.RESULTS = this.dataFormItem.dataFormResults;
      this.dataFormItem.RESULT = null;
    }

    else if (
      type == this.formsGlobals.CONTROL_NUMERIC_DRAG_INPUT_SLIDER ||
      type == this.formsGlobals.CONTROL_NUMERIC_SLIDER
    ) {
      let result: FormResultDataModel;
      if (this.dataFormItem.dataFormResults.length > 1) {
        for (let i = 1; i < this.dataFormItem.dataFormResults.length; i++) {
          this.dataFormItem.dataFormResults[i].xauditDeleted = true;
          this.saveResult(this.dataFormItem.dataFormResults[i]);
        }
        this.filterResults();
        result = this.dataFormItem.dataFormResults.splice(0, 1)[0];
        this.dataFormItem.dataFormResults.forEach(result => {
          result.xauditDeleted = true;
        });
      } else if (this.dataFormItem.dataFormResults.length === 1) {
        result = this.dataFormItem.dataFormResults[0];
      } else {
        result = this.createBlankResult();
        if (this.dataFormItem.cfgDFormItem.DefaultValue != null && this.dataFormItem.cfgDFormItem.DefaultValue !== "") {
          result.Result = this.dataFormItem.cfgDFormItem.DefaultValue;
        }
      }

      if (isNaN(parseFloat(result.Result))) {
        result.NumResult = 0;
      } else {
        result.NumResult = parseFloat(result.Result)
      }
      this.dataFormItem.RESULT = result
      this.dataFormItem.RESULTS = null;
    }

    if (this.dataFormItem.cfgDFormItem.isRequired) {
      let itemRequired = false;
      if (this.dataFormItem.RESULT !== null)
        if (this.dataFormItem.RESULT?.NumResult == null && (this.dataFormItem.RESULT?.Result == "" || this.dataFormItem.RESULT?.Result == null))
          itemRequired = true;
      if (this.dataFormItem.RESULTS !== null)
        if (this.dataFormItem.RESULTS?.length == 0 || this.dataFormItem.RESULTS?.filter(r => r.NumResult != null || r.Result != "").length == 0)
          itemRequired = true;
      if (itemRequired) {
        this.messagingService.sendComponentMessage(`FORMS_ITEM_REQUIRED_${this.CaseWorkItemFormsId}`, { required: true, dataFormItemId: this.dataFormItem.dataFormItemId });
      }
    }
  }

  updateVitalSigns() {
    let one = this.dataFormItem.RESULT_VITAL_ONE;
    let two = this.dataFormItem.RESULT_VITAL_TWO;
    let three = this.dataFormItem.RESULT_VITAL_THREE;
    let total = `${one}/${two}/${three}`;
    this.dataFormItem.RESULT.Result = total;
  }

  updateBloodPressure() {
    let top = this.dataFormItem.RESULT_TOP;
    let bottom = this.dataFormItem.RESULT_BOTTOM;
    let total = `${top}/${bottom}`;
    this.dataFormItem.RESULT.Result = total;
  }

  checkIfAbsoluteOptionIsInResult(option: string): boolean {
    let flag = false;
    this.dataFormItem.RESULTS.forEach(result => {
      if (result.xauditDeleted == false && result.Result === option) {
        flag = true;
      }
    })
    return flag;
  }

  checkIfOptionIsInResult(option: EnumValidValueItemModel): boolean {
    let flag = false;
    this.dataFormItem.RESULTS.forEach(result => {
      if (result.Result === option.ValidValueItemId) {
        flag = true;
      }
    })
    return flag;
  }

  setSingleResult(option: EnumValidValueItemModel) {
    if (this.isLocked) {
      return;
    }
    this.dataFormItem.RESULT.Result = option.ValidValueItemId;
    this.sendItemResult(this.dataFormItem, option.ValidValueItemId);
  }

  setAbsoluteSingleResult(result) {
    this.dataFormItem.RESULT.Result = result;
  }

  setAbsoluteMultiResult(option: string, remove: boolean) {
    if (remove) {
      this.dataFormItem.RESULTS.forEach(result => {
        if (result.Result === option) {
          result.xauditDeleted = true;
        }
      });
    } else {
      let result = this.createBlankResult();
      result.Result = option;
      this.dataFormItem.RESULTS.push(result);
    }
  }

  setMultiResult(option: EnumValidValueItemModel, remove: boolean) {
    if (remove) {
      this.dataFormItem.RESULTS.forEach(result => {
        if (result.Result === option.ValidValueItemId) {
          result.xauditDeleted = true;
        }
      });
    } else {
      let result = this.createBlankResult();
      result.Result = option.ValidValueItemId;
      this.dataFormItem.RESULTS.push(result);
    }
    this.sendItemResult(this.dataFormItem, option.ValidValueItemId, false);
  }

  createBlankResult(): FormResultDataModel {
    let result = new FormResultDataModel();
    result.dataFormItemId = this.dataFormItem.dataFormItemId;
    result.Result = '';
    result.xauditDeleted = false;
    return result;
  }

  filterResults() {
    this.dataFormItem.dataFormResults = this.dataFormItem.dataFormResults
      .filter(result => { return result.xauditDeleted == false });
  }

  signatureDrawStart() {
    this.signatureDrawn = true;
    
    // Ensure the signature pad is properly sized when drawing starts
    if (this.signaturePad && this.signaturePad.queryPad) {
      const canvas = this.signaturePad.queryPad()._canvas;
      
      // For iPad: ensure touch events work correctly
      if (this.isIOSDevice()) {
        canvas.style.touchAction = 'none';
        canvas.style.msTouchAction = 'none';
        
        // Prevent scrolling on the page while signing on iOS
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        
        // Reset overflow when drawing is complete
        const resetBodyOverflow = () => {
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';
          canvas.removeEventListener('touchend', resetBodyOverflow);
        };
        
        canvas.addEventListener('touchend', resetBodyOverflow);
      }
      
      // Check if the canvas needs resizing
      if (canvas.width !== this.signaturePadOptions['canvasWidth'] || 
          canvas.height !== this.signaturePadOptions['canvasHeight']) {
        this.resizeSignaturePad();
      }
    }
  }
  saveResult(result) {

    let promise = new Promise((resolve, reject) => {
      this.formsService.saveResult(result)
        .subscribe(response => {
          this.messagingService.sendComponentMessage(MessagingConstants.FORM_ITEM_SAVED, {});

          // If signature control, we want to update the case view with the status and signature icon in real time.
          if (this.dataFormItem.cfgDFormItem.enumFormControlID === '00000001-1000-0000-0000-000000000027') {
            this.messagingService.sendComponentMessage(MessagingConstants.CASES_REFRESH, {});
          }
          //check if saved value cleared required or not
          if (this.dataFormItem.cfgDFormItem.isRequired) {
            this.messagingService.sendComponentMessage(`FORMS_ITEM_REQUIRED_${this.CaseWorkItemFormsId}`, { required: this.isEmptyField(), dataFormItemId: this.dataFormItem.dataFormItemId });
          }
          resolve(response);
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  isEmptyField() {
    return (this.dataFormItem.RESULT !== null && this.dataFormItem.RESULT?.NumResult == null && (this.dataFormItem.RESULT?.Result == "" || this.dataFormItem.RESULT?.Result == null))
      || (this.dataFormItem.RESULTS !== null && (this.dataFormItem.RESULTS.length == 0 || this.dataFormItem.RESULTS.filter(r => r.NumResult != null || r.Result != "").length == 0))
  }
  getDisplayNameForItemResult() {
    if (this.dataFormItem.cfgDFormItem.Label) {
      return this.dataFormItem.cfgDFormItem.Label;
    }
    if (this.dataFormItem.RESULT) {
      let displayString = '';
      this.dataFormItem.cfgDFormItem.ValidValue.enumValidValueItems.forEach(item => {
        if (item.ValidValueItemId === this.dataFormItem.RESULT.Result) {
          displayString = item.ItemDisplayValue;
        }
      });
      return displayString;
    } else {
      return this.dataFormItem.cfgDFormItem.Label;
    }
  }

  saveItem() {
    let type = this.dataFormItem.cfgDFormItem.enumFormControlID;
    let promises = [];

    if (this.dataFormItem.RESULT != null) {
      if (type == this.formsGlobals.CONTROL_NUMERIC_DRAG_INPUT_SLIDER) {
        this.dataFormItem.RESULT.Result = `${this.dataFormItem.RESULT.NumResult}`;
      }
      promises.push(this.saveResult(this.dataFormItem.RESULT));
    } else if (this.dataFormItem.RESULTS != null) {
      this.dataFormItem.RESULTS.forEach(result => {
        promises.push(this.saveResult(result));
      });
    }

    this.messagingService.sendComponentMessage(`FORMS_ITEM_NEEDS_SAVE_${this.CaseWorkItemFormsId}`, {});
    Promise.all(promises)
      .then(result => {

        this.messagingService.sendComponentMessage(`FORMS_ITEM_HAS_SAVED_${this.CaseWorkItemFormsId}`, {});

      }, err => {
        this.messagingService.sendComponentMessage(`FORMS_ITEM_HAS_SAVED_${this.CaseWorkItemFormsId}`, {});
      });
  }

  formatDateTime() {
    let nowDate = new Date();
    let itemDate = new Date(this.dataFormItem.RESULT.Result);

    let nowYear = nowDate.getFullYear();
    let nowDay = nowDate.getDate();
    let nowMonth = nowDate.getMonth();

    let itemYear = itemDate.getFullYear();
    let itemMonth = itemDate.getMonth();
    let itemDay = itemDate.getDate();

    if (
      nowYear === itemYear &&
      nowDay === itemDay &&
      nowMonth === itemMonth
    ) {
      this.dataFormItem.RESULT.Result = nowDate.toString();
      this.dataFormItem.RESULT_HOUR = nowDate.getHours();
      this.dataFormItem.RESULT_MINUTES = nowDate.getMinutes();
    }
  }

  validateTime(selection) {
    if (selection === 'hours') {
      let hourInt = parseInt(this.dataFormItem.RESULT_HOUR);
      if (isNaN(hourInt)) {
        this.dataFormItem.RESULT_HOUR = null;
        this.dataFormItem.RESULT_HOUR = 0;
        return;
      }

      if (hourInt < 0) {
        this.dataFormItem.RESULT_HOUR = null;
        this.dataFormItem.RESULT_HOUR = 0;
        return;
      }

      if (hourInt > 23) {
        this.dataFormItem.RESULT_HOUR = null;
        this.dataFormItem.RESULT_HOUR = 23;
        return;
      }
    } else if (selection === 'minutes') {
      let minuteInt = parseInt(this.dataFormItem.RESULT_MINUTES);
      if (isNaN(minuteInt)) {
        this.dataFormItem.RESULT_MINUTES = 0;
        return;
      }

      if (minuteInt < 0) {
        this.dataFormItem.RESULT_MINUTES = 0;
        return;
      }

      if (minuteInt > 59) {
        this.dataFormItem.RESULT_MINUTES = 59;
        return;
      }
    }
  }

  updateItemDate(selection) {
    let resultDate = new Date(this.dataFormItem.RESULT.Result);
    if (selection === 'hours') {
      let hourInt = parseInt(this.dataFormItem.RESULT_HOUR);
      resultDate.setHours(hourInt);
    } else if (selection === 'minutes') {
      let minuteInt = parseInt(this.dataFormItem.RESULT_MINUTES);
      resultDate.setMinutes(minuteInt);
    }
    this.dataFormItem.RESULT.Result = resultDate.toString();
  }

  getRowHeaderLabelOrResult() {
    let results = this.dataFormItem.dataFormResults.filter(r => { return !r.xauditDeleted });
    if (results && results.length) {
      return results[0].Result;
    } else {
      return this.dataFormItem.cfgDFormItem.Label;
    }
  }

  async performActionFromButton() {
    if (!this.actionsEnabled) { return; }

    if (this.dataFormItem.cfgDFormItem.AfterUpdateItemAction && this.dataFormItem.cfgDFormItem.AfterUpdateItemAction.cfgItemActionSteps) {
      
      this.isActionExecuting = true;

      this.dataFormItem.cfgDFormItem.AfterUpdateItemAction.cfgItemActionSteps = this.dataFormItem.cfgDFormItem.AfterUpdateItemAction.cfgItemActionSteps.sort((a, b) => a.StepOrder - b.StepOrder);

      let result: string = '';

      for (const step of this.dataFormItem.cfgDFormItem.AfterUpdateItemAction.cfgItemActionSteps) {
        try {
          result = await this.performActionStep(step, result) as any;
        } catch {
        }
      }
      this.isActionExecuting = false;
    }
  }

  openWorkAreaModal() {
    let promise = new Promise((resolve, reject) => {

      const dialogRef = this.dialog.open(FormsItemModalComponent, {
        panelClass: 'dialog',
        data: {
          type: 'WORKAREA',
          actionId: this.dataFormItem.cfgDFormItem.AfterUpdate_ItemActionId,
          dFormParagraphId: this.relatedRepeatingParagraph.DFormParagraphId
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          resolve(result);
        } else {
          reject();
        }
      }, err => {
        reject(err);
      });

    });
    return promise;
  }

  async performActionStep(step: FormItemActionStepConfigurationModel, prevResult: any) {
    switch (this.dataFormItem.cfgDFormItem.AfterUpdateItemAction.ActionName) {
      case FormActionNameEnum.CREATE_NOTE:
        this.saveDataNoteEvent.emit(this.dataFormItem.cfgDFormItem.PostLabel);
        return;
      case FormActionNameEnum.REPEAT_PARAGRAPH:
        const copyData = {
          paragraphToCopy: this.dataFormItem.dataFormParagraphID
        };
        this.messagingService.sendComponentMessage(`FORMS_PARAGRAPH_IS_SAVING_${this.CaseWorkItemFormsId}`, { isSaving: true });
        await this.createCopyOfParagraph(copyData);
        return;

    }

    let promise = new Promise((resolve, reject) => {
      if (step.ActionParam.indexOf('GETWORKAREA') > -1) {
        this.openWorkAreaModal()
          .then(result => {
            const obj = {
              getWorkArea: true,
              result
            };
            resolve(obj);
          }, err => {
            reject(err);
          });
      } else
        if (prevResult.getWorkArea) {
          const teeth: ToothUiManagerModel[] = prevResult.result.data;
          for (const tooth of teeth) {
            const copyData = {
              paragraphToCopy: prevResult.result.dFormParagraphId,
              toothNumber: tooth.toothModel.ToothId
            };
            this.messagingService.sendComponentMessage(`FORMS_PARAGRAPH_IS_SAVING_${this.CaseWorkItemFormsId}`, { isSaving: true });
            this.createCopyOfParagraph(copyData)
              .then((paragraph: FormParagraphDataModel) => {
                if (paragraph) {
                  //let dataParagraph: dataFormParagraph4Forms = paragraph;
                  paragraph.dataFormItems = paragraph.dataFormItems.sort((a, b) => 
                    { return a.cfgDFormItem.ItemOrder - b.cfgDFormItem.ItemOrder });
                      
                  if (paragraph.dataFormItems.length) {
                    const idx = paragraph.dataFormItems.findIndex(model => model.ItemName === 'resToothNum');
                    paragraph.dataFormItems[idx].dataFormResults = [];
                    let result: FormResultDataModel = this.createBlankResult();
                    result.dataFormItemId = paragraph.dataFormItems[idx].dataFormItemId;
                    result.Result = tooth.toothModel.cfgToothDescriptions[0].Notation;
                    // Why save the result right after creating it?
                    this.saveResult(result)
                      .then((response: FormResultDataModel) => {
                        this.messagingService.sendComponentMessage(`FORMS_PARAGRAPH_IS_SAVING_${this.CaseWorkItemFormsId}`, { isSaving: false });
                        if (response) {
                          paragraph.dataFormItems[idx].dataFormResults.push(response);
                          resolve(paragraph);
                        } else {
                          reject();
                        }
                      }, err => {
                        this.messagingService.sendComponentMessage(`FORMS_PARAGRAPH_IS_SAVING_${this.CaseWorkItemFormsId}`, { isSaving: false });
                        reject(err);
                      });
                  }
                }
              }, err => {
                this.messagingService.sendComponentMessage(`FORMS_PARAGRAPH_IS_SAVING_${this.CaseWorkItemFormsId}`, { isSaving: false });
                reject(err);
              });
          }
        }
    });
    return promise;
  }

  setActionButtonsVisibility() {
    // console.log('FormsItemComponent setActionButtonsVisibility');
    const collection = document.querySelectorAll<HTMLElement>(".form-item-action-button");
    // console.log('setActionButtonsVisibility collection', collection);
    for (let i = 0; i < collection.length-1; i++) {
      collection[i].style.display = "none";
    }
    if (collection.length > 0) 
      collection[collection.length-1].style.display = "block";
    const deleteActions = document.querySelectorAll<HTMLElement>(".form-item-action-button-delete");
    // console.log('setActionButtonsVisibility deleteActions', deleteActions);
    if (deleteActions.length === 1) {
      deleteActions[0].style.display = "none";
    }

  }

  createCopyOfParagraph(copyData) {
    const sendObj = {
      paragraphOrder: this.paragraphOrder,
      DFormParagraphId: copyData.paragraphToCopy,
      CaseWorkItemFormsId: this.CaseWorkItemFormsId,
      toothNumber: copyData.toothNumber
    };
    let promise = new Promise((resolve, reject) => {
      this.formsService.duplicateParagraph(sendObj)
        .subscribe(response => {
          this.setActionButtonsVisibility();
          this.messagingService.sendComponentMessage('FORMS_RELOAD_CURRENT_FORM',{});
          resolve(response);
        }, err => {
          console.log(err);
          reject(err);
        });
    });
    return promise;
  }

  deleteParagraph(dataFormParagraphID: string) {
    this.isActionExecuting = true;

    this.formsService.deleteParagraph(dataFormParagraphID)
      .subscribe(response => {
        // console.log('deleteParagraph response', response);
        this.isActionExecuting = false;
        this.messagingService.sendComponentMessage('FORMS_RELOAD_CURRENT_FORM',{});

        setTimeout(this.setActionButtonsVisibility, 500);
      }, error => {
        console.log(error);
      });
  }


  phoneInputChanged(value) {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
      this.inputTimeout = false;
    }

    this.inputTimeout = setTimeout(_ => {
      this.inputTimeout = false;
      this.dataFormItem.RESULT.Result = ' ';
      this.dataFormItem.RESULT.Result = `0${this.phoneFormat(value)}0`;
      this.dataFormItem.RESULT.Result = this.dataFormItem.RESULT.Result.slice(1, this.dataFormItem.RESULT.Result.length - 1);
    }, 1500);

  }

  phoneFormat(input) {
    input = input.replace(/\D/g, '');

    if (!input || input.length < 10) {
      return input + ' ';
    }

    var startIndex = 0;
    var numbersBefore = '';

    if (input.length > 10) {
      startIndex = input.length - 10;
      numbersBefore = input.substring(0, startIndex);
      input = input.substring(startIndex, input.length);
    } else {
      input = input.substring(0, 10);
    }

    var size = input.length;
    if (size == 0) {
      input = input;
    } else if (size < 4) {
      input = '(' + input;
    } else if (size < 7) {
      input = '(' + input.substring(0, 3) + ') ' + input.substring(3, 6);
    } else {
      input = '(' + input.substring(0, 3) + ') ' + input.substring(3, 6) + ' - ' + input.substring(6, 10);
    }

    if (startIndex == 0) {
      return input;
    } else {
      return `+${numbersBefore} ${input}`;
    }
  }

  sendItemResult(dataFormItem: FormItemDataModel, value: string, overwriteCurrentResult = true) {
    let model: FormResultDataModel = null;
    if (overwriteCurrentResult) {
      if (dataFormItem.dataFormResults.length > 0) {
        model = dataFormItem.dataFormResults.find(p => p.dataFormItemId === dataFormItem.dataFormItemId);
      }
    }

    if (!model || !overwriteCurrentResult) {
      model = new FormResultDataModel();
      model.dataFormItemId = dataFormItem.dataFormItemId;
      dataFormItem.dataFormResults.push(model);
    }

    model.Result = value;
    this.saveResult(model);/*.subscribe((response) => {
      model = response;
      console.log(response);
    });*/
  }

  // Autosave
  onComboListSelectionChange(event: MatSelectChange, dataFormItem: FormItemDataModel) {
    this.sendItemResult(dataFormItem, event.value.ValidValueItemId);
  }

  onNumericInputChange(event: any, dataFormItem: FormItemDataModel) {
    this.sendItemResult(dataFormItem, event.target.value.toString());
  }

  // Services
  getMacroNotes() {
    this.notesService.getNoteBoilerplateSet().subscribe((notes) => {
      this.dataNoteSet = notes;
      this.notesDropdownData = this.dataNoteSet.dataPredefinedNotes.map((p) => {
        const ddm = new TdoDropdownModel();
        ddm.label = p.NoteDescription;
        ddm.value = p;
        return ddm;
      });
      if (this.dataNoteSet.dataPredefinedNotes.length > 0) {
        this.selectedPredefinedNote = this.dataNoteSet.dataPredefinedNotes[0];
      }
      // this.isLoading = false;
    });
  }

  // Notes
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  onMacroNoteSelection(event: MatSelectChange) {
    this.selectedPredefinedNote = event.value;
  }

  onCreateNoteClick() {
    const model = new TdoNoteSaveModel();
    model.text = this.selectedPredefinedNote.NoteTemplate;
    model.patientId = this.globals.currentPatientId;

    const associatedObjects = [];
    const newAssociatedObjectCase = new TdoNoteAssociatedObject();
    newAssociatedObjectCase.objectId = this.globals.currentPatientCaseId;
    newAssociatedObjectCase.parentObjectName = 'dataPatientCase';
    associatedObjects.push(newAssociatedObjectCase);

    model.associatedObjects = associatedObjects;
    this.messagingService.sendComponentMessage(MessagingConstants.NOTES_CREATE_NOTE, model);
  }

  // Image Component
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  onAddImageClick(): void {
    if (this.isLocked) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  onImageSelected(): void {
    const file: File = this.fileInput.nativeElement.files[0];
    this.uploadDocuments([file]);
  }

  filesDropped(event: ITdoFileHandle[]): void {
    if (this.isLocked) {
      return;
    }
    const file: File = event[0].file;
    this.uploadDocuments([file]);
  }

  uploadDocuments(files: File[]) {
    /*console.log('File type: ' + files[0].type);
    return;*/
    this.isUploading = true;

    this.fileHelperService.getDataAndThumbnailFromFile(files[0]).subscribe((mod) => {
      const fileModel = mod;
      const uploadModel = new DocumentUploadModel();
      uploadModel.blob = fileModel.fileDataBlob;
      uploadModel.blobThumbnail = fileModel.thumbnailDataBlob;
      uploadModel.docClassId = DocumentClassEnum.Original;
      uploadModel.docTypeId = DocumentTypeEnum.MedicalImaging;
      uploadModel.hasAnnotation = false;
      uploadModel.docTagIds = ['0000001d-1000-0000-0000-000000000121'];

      switch (fileModel.fileMimeType) {
        case FileHelperService.MIME_JPG:
        case FileHelperService.MIME_PNG:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.JPG;
          break;
        case FileHelperService.MIME_AVI:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.AVI;
          break;
        case FileHelperService.MIME_MOV:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.MP4;
          break;
        case FileHelperService.MIME_MP4:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.MP4;
          break;
        case FileHelperService.MIME_PDF:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.PDF;
          break;
        case FileHelperService.MIME_MP3:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.MP3;
          break;
        case FileHelperService.MIME_WAV:
          uploadModel.docFileTypeId = DocumentFileTypeEnum.WAV;
          break;
      }

      uploadModel.patientId = this.globals.currentPatientId;
      uploadModel.patientCaseId = this.globals.currentPatientCaseId;

      this.fileService.uploadDocuments([uploadModel]).subscribe((response) => {
        const res = response.documents[0];
        this.fileService.addMultiDocumentsTags([res.DocumentID], ['0000001d-1000-0000-0000-000000000121']).subscribe((responseAddTag) => {
          this.sendItemResult(this.dataFormItem, res.DocumentFileName, false);
          this.isUploading = false;
        });
      });
    });
  }

  onImageListClick(result: FormResultDataModel) {
    this.matDialog.open(FormsFullSizeImageDialogComponent, { data: result.Result }).afterClosed().subscribe((res) => {
      //
    });
  }

  onCheckBoxClick(event: MatCheckboxChange) {
    let value = '';
    if (event.checked) {
      value = 'true';
      if (this.dataFormItem.dataFormResults && this.dataFormItem.dataFormResults.length > 0) {
        this.dataFormItem.dataFormResults[0].Result = 'true';
      } else {

      }
    } else {
      value = 'false';
      this.dataFormItem.dataFormResults[0].Result = 'false';
    }

    this.sendItemResult(this.dataFormItem, value);
  }

  deleteImageListItem(result: FormResultDataModel) {
    if (this.isLocked) {
      return;
    }
    this.formsService.deleteResult(result.dataFormResultId).subscribe((res) => {
      const idx = this.dataFormItem.dataFormResults.findIndex(p => p.dataFormResultId === result.dataFormResultId);
      if (idx > -1) {
        this.dataFormItem.dataFormResults.splice(idx, 1);
      }
    });
  }

  onTextInputFinished(event: any) {
    this.sendItemResult(this.dataFormItem, this.dataFormItem.RESULT.Result);
  }

  getValidValueItems() {
    //dataFormItem.cfgDFormItem.ValidValue.enumValidValueItems
    const items = this.staticDataService.getValidValueItemsByValidValueId(this.dataFormItem.cfgDFormItem.ValidValueId);
    return items;
  }

  getFloatLabelValue() {
    if (this.dataFormItem.cfgDFormItem.ParagraphRowHeight < 30) {
      return 'never';
    } else {
      return 'auto';
    }
  }

  onDatePickerChange(event: Event) {
    console.log('test');
  }

  onDatePickerClosed(event: void) {
    // console.log(this.dataFormItem.RESULT.Result);
    this.onTextInputFinished(this.dataFormItem.RESULT.Result);
  }

  /**
   * Handle window resize events to adjust signature pad size
   */
  handleWindowResize() {
    // Debounce the resize to avoid excessive calculations
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.resizeTimeout = setTimeout(() => {
      this.setupSignaturePadSize();
      this.resizeSignaturePad();
    }, 250);
  }

  /**
   * Calculate and set up the signature pad size based on screen width
   */
  setupSignaturePadSize() {
    // Get current viewport width
    const screenWidth = window.innerWidth;
    let containerWidth;
    
    // Check if this is the only element in the row (based on paragraph column)
    const isOnlyElementInRow = this.dataFormItem?.cfgDFormItem?.ParagraphColumn === 0;
    
    // Check if device is iPad
    const isIPad = this.isIOSDevice() && window.innerWidth >= 768 && window.innerWidth <= 1366;
    
    // Adjust signature pad width based on screen size and whether it's alone in a row
    if (screenWidth <= 768) {
      // Mobile/small tablet
      containerWidth = isOnlyElementInRow 
        ? Math.min(Math.max(screenWidth * 0.9, 300), 450) // Wider when alone (90% of screen)
        : Math.min(Math.max(screenWidth * 0.75, 250), 350);
    } else if (screenWidth <= 1024) {
      // Tablet
      containerWidth = isOnlyElementInRow
        ? Math.min(Math.max(screenWidth * 0.8, 450), 650) // Wider when alone (80% of screen)
        : Math.min(Math.max(screenWidth * 0.65, 350), 500);
    } else if (screenWidth <= 1366) {
      // Small desktop
      containerWidth = isOnlyElementInRow
        ? Math.min(Math.max(screenWidth * 0.7, 550), 850) // Wider when alone (70% of screen)
        : Math.min(Math.max(screenWidth * 0.5, 400), 600);
    } else {
      // Large desktop
      containerWidth = isOnlyElementInRow
        ? Math.min(Math.max(screenWidth * 0.6, 600), 1000) // Wider when alone (60% of screen)
        : Math.min(Math.max(screenWidth * 0.4, 500), 700);
    }
    
    // iPad-specific adjustment - ensure minimum width for better visibility
    if (isIPad) {
      containerWidth = Math.max(containerWidth, 350);
    }
    
    // If the form item has a specific width constraint, respect it
    if (this.dataFormItem?.cfgDFormItem?.ParagraphColumnWidth > 0) {
      const formItemWidth = this.dataFormItem.cfgDFormItem.ParagraphColumnWidth;
      // Use the smaller of the calculated width or the form item width
      containerWidth = Math.min(containerWidth, formItemWidth - 10);
    }
    
    // If it's alone in the row and has no specific width constraint, make it wider 
    // by checking if there are other items in the same row with the same paragraph ID
    if (isOnlyElementInRow && this.dataFormItem?.dataFormParagraphID) {
      // Make it at least 60% of screen width, but respect max constraints for different screen sizes
      containerWidth = Math.max(containerWidth, screenWidth * 0.6);
    }
    
    // Update signature pad options with calculated width
    this.signaturePadOptions['canvasWidth'] = Math.floor(containerWidth);
    
    // Adjust height based on width to maintain a good aspect ratio when alone in row
    // Use a more appropriate aspect ratio for iPad to ensure it's tall enough for finger signing
    const aspectRatio = isIPad ? 2.8 : (isOnlyElementInRow ? 3.5 : 2.5);
    const calculatedHeight = containerWidth / aspectRatio;
    
    // Use either calculated height based on aspect ratio or the configured height, whichever is larger
    // Ensure minimum height for iPad
    const minHeight = isIPad ? 150 : 120;
    const height = this.dataFormItem?.cfgDFormItem?.ParagraphRowHeight > 0 
      ? Math.max(calculatedHeight, this.dataFormItem.cfgDFormItem.ParagraphRowHeight - 20, minHeight)
      : Math.max(calculatedHeight, minHeight);
      
    this.signaturePadOptions['canvasHeight'] = Math.floor(height);
    
    // For debugging
    if (isIPad) {
      console.log('iPad signature pad dimensions:', 
                 'width:', this.signaturePadOptions['canvasWidth'], 
                 'height:', this.signaturePadOptions['canvasHeight']);
    }
  }

  /**
   * Resize the signature pad canvas if it exists
   */
  resizeSignaturePad() {
    if (this.signaturePad && this.signaturePad.queryPad && this.signaturePad.queryPad()._canvas) {
      const canvas = this.signaturePad.queryPad()._canvas;
      const context = canvas.getContext('2d');
      
      // Save any existing content if needed
      let imageData = null;
      if (context && !this.resetSignature && this.signatureDrawn) {
        try {
          imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        } catch (e) {
          console.error('Could not save signature data during resize:', e);
        }
      }
      
      // Set the canvas dimensions
      canvas.width = this.signaturePadOptions['canvasWidth'];
      canvas.height = this.signaturePadOptions['canvasHeight'];
      
      // Apply explicit styles for iPads to ensure proper rendering
      if (this.isIOSDevice()) {
        // Apply explicit styles for iOS devices
        canvas.style.width = `${canvas.width}px`;
        canvas.style.height = `${canvas.height}px`;
        canvas.style.touchAction = 'none';
        canvas.style.msTouchAction = 'none';
        canvas.style.userSelect = 'none';
        canvas.style.webkitUserSelect = 'none';
        canvas.style.msUserSelect = 'none';
        
        // For debugging
        console.log('Applied iOS-specific canvas styles');
      }
      
      // Clear and redraw if there was content
      if (this.signatureDrawn) {
        if (imageData && context) {
          try {
            // Try to restore previous signature data with proper scaling
            context.putImageData(imageData, 0, 0);
          } catch (e) {
            console.error('Could not restore signature data after resize:', e);
            this.signaturePad.clear();
            this.signatureDrawn = false;
          }
        } else {
          this.signaturePad.clear();
          this.signatureDrawn = false;
        }
      }
    }
  }

  /**
   * Check if current device is iOS (iPad/iPhone)
   */
  isIOSDevice(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /ipad|iphone|ipod|macintosh/.test(userAgent) && 'ontouchend' in document;
  }

  signName() {
    console.log('signName: ', this.currentPatientName);
    this.signaturePad.clear();
    const canvas = this.signaturePad.queryPad()._canvas;
    console.log('canvas.height', canvas.height);
    console.log('canvas.width', canvas.width);
    const context = canvas.getContext('2d')!;

    context.font = '36px script, verdana, sans-serif';
    context.textBaseline = "middle";
    context.textAlign = "center";
    context.fillText(this.currentPatientName.toUpperCase(), canvas.width/2, canvas.height/2,canvas.width-20);
    this.signatureDrawn = true;
    this.signatureDrawComplete();
    
  }
}
